import {
  AdminOrganizationEmployeeNode,
  AuthAccountContactTypeChoices,
  AuthAccountOrganizationEmployeeCooperationStatusChoices,
  EmployeeType,
  Typography,
  useFetchFile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { User } from "@heroui/react";
import { employeeStatusConfig } from "@modules/company/constants/employeeStatusConfig";
import { ColumnDef } from "@tanstack/react-table";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);
dayjs.extend(customParseFormat);

const styles = {
  header: "text-sm font-thin",
  subHeader: "text-xs font-thin",
  cell: "text-sm",
  badge: "p-1 text-xs text-white rounded-md",
  statusContainer: "flex items-center gap-2 px-3 py-2 rounded-md",
  cooperationContainer: "flex gap-2",
} as const;

interface StatusBadgeProps {
  status: AuthAccountOrganizationEmployeeCooperationStatusChoices;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { label, icon: Icon, color, iconColor } = employeeStatusConfig[status];

  return (
    <div className={styles.statusContainer}>
      <Icon className="w-6 h-6" fill={iconColor} />
      <span className={`font-medium ${color}`}>{label}</span>
    </div>
  );
};

interface NameCellProps {
  employee?: EmployeeType | null;
}

const NameCell: React.FC<NameCellProps> = ({ employee }) => {
  const { data: avatarUrl } = useFetchFile({
    urlPath: employee?.profile?.avatar?.file,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <User
      name={`${employee?.firstName || "-"} ${employee?.lastName || "-"}`}
      description={employee?.profile?.score}
      avatarProps={{
        src: avatarUrl,
        isDisabled: !avatarUrl,
        classNames: {
          img: "opacity-1",
        },
      }}
    />
  );
};

interface ContactCellProps {
  email?: string | null;
  phone?: string | null;
}

const ContactCell: React.FC<ContactCellProps> = ({ email, phone }) => (
  <>
    <Typography className={styles.cell}>{email || "-"}</Typography>
    <Typography className={styles.cell}>{phone || "-"}</Typography>
  </>
);

interface CareerCellProps {
  title?: string | null;
  startAt?: string | null;
}

// eslint-disable-next-line react-refresh/only-export-components
const CareerCell: React.FC<CareerCellProps> = ({ title, startAt }) => {
  const now = dayjs();
  const start = dayjs(startAt);

  // Check if start date is in the future
  const hasNotStarted = start.isAfter(now);

  // Only calculate duration if already started
  const getDuration = () => {
    if (hasNotStarted) return null;

    const years = now.diff(start, "year");
    const months = now.subtract(years, "year").diff(start, "month");
    const days = now.subtract(years, "year").subtract(months, "month").diff(start, "day");

    // Format the duration text
    if (years === 0 && months === 0) {
      return `Been with company for ${days} days`;
    } else if (years === 0) {
      return `Been with company for ${months} months, ${days} days`;
    } else {
      return `Been with company for ${years} years, ${months} months`;
    }
  };

  return (
    <div>
      <Typography className={`${styles.cell} text-zinc-600`}>{title || "-"}</Typography>
      <Typography className="text-xs text-zinc-600">{hasNotStarted ? "Starting soon" : getDuration()}</Typography>
    </div>
  );
};

interface CooperationCellProps {
  locationType?: string | null;
  contractType?: string | null;
}

const CooperationCell: React.FC<CooperationCellProps> = ({ locationType, contractType }) => (
  <div className={styles.cooperationContainer}>
    {locationType && <Typography className={`${styles.badge} bg-[#C0C2AE]`}>{locationType}</Typography>}
    {contractType && <Typography className={`${styles.badge} bg-zinc-500`}>{contractType}</Typography>}
    {!locationType && !contractType && <Typography className="text-center w-full">-</Typography>}
  </div>
);

export const getEmployeesColumns = (): ColumnDef<AdminOrganizationEmployeeNode>[] => [
  {
    id: "num",
    header: () => <Typography className={styles.header}>Num</Typography>,
    cell: ({ row }) => <Typography className={styles.header}>{row.index + 1}</Typography>,
  },
  {
    id: "user",
    header: () => (
      <div>
        <Typography className={styles.header}>Employee Name</Typography>
        <Typography className={styles.subHeader}>Score</Typography>
      </div>
    ),
    cell: ({ row }) => <NameCell employee={row.original.employee} />,
  },
  {
    id: "organization",
    header: () => (
      <div>
        <Typography className={styles.header}>Email</Typography>
        <Typography className={styles.subHeader}>Phone</Typography>
      </div>
    ),
    cell: ({ row }) => {
      const phoneContact = row.original.employee?.profile?.contacts?.find(
        (contact) => contact?.type === AuthAccountContactTypeChoices.PHONE,
      );
      return <ContactCell email={row.original.employee?.email} phone={phoneContact?.value} />;
    },
  },
  {
    id: "career",
    accessorKey: "industry.title",
    header: () => (
      <div>
        <Typography className={styles.header}>Career field</Typography>
        <Typography className={styles.subHeader}>Career history</Typography>
      </div>
    ),
    cell: ({ row }) => (
      <CareerCell title={row.original.cooperation?.jobPosition?.title} startAt={row.original.cooperation?.startAt} />
    ),
  },
  {
    id: "status",
    accessorKey: "status",
    header: () => <Typography className={styles.header}>Status</Typography>,
    cell: ({ row }) => (
      <StatusBadge
        status={row.original.cooperation?.status || AuthAccountOrganizationEmployeeCooperationStatusChoices.ACTIVE}
      />
    ),
  },
  {
    id: "cooperation",
    accessorKey: "cooperation",
    header: () => <Typography className={styles.header}>type of cooperation</Typography>,
    cell: ({ row }) => (
      <CooperationCell
        locationType={row.original.cooperation?.jobPosition?.locationType}
        contractType={row.original.cooperation?.jobPosition?.contractType}
      />
    ),
  },
];
